import { ReactElement } from 'react';
import { Tooltip } from 'react-tooltip';
import {
  IconButtonClassnameProps,
  IconButtonProps,
  iconSizeMap,
  sizeMap,
} from './IconButton';

const typeMap = {
  primary: 'bg-primary-400 hover:bg-primary-300 active:enabled:shadow-focused',
  secondary: 'bg-grey-700 hover:bg-grey-800 active:enabled:shadow-focused',
  subtle: 'hover:bg-black-t-900 active:enabled:shadow-focused',
  delete: 'hover:bg-red-t-1000 active:enabled:shadow-focused',
  tertiary:
    'border border-black-t-600 border-inset hover:bg-black-t-800 active:enabled:shadow-focused disabled:border-none',
  basic:
    'hover:bg-black-t-900 active:enabled:shadow-focused [&[data-active]:not([data-active="false"])]:bg-black-t-900',
  light: 'bg-black-t-900 hover:bg-black-t-800 active:enabled:shadow-focused',
};

const typeMapDisabled = {
  primary: 'disabled:bg-background-disabled',
  secondary: 'disabled:bg-background-disabled',
  subtle: '',
  delete: '',
  tertiary: '',
  basic: '',
  light: 'disabled:bg-background-disabled',
};

export const getIconButtonInvertedStyles = ({
  type,
  size,
  disabled,
  additionalClassnames,
  accentColour,
}: IconButtonClassnameProps & {
  additionalClassnames?: string | undefined;
  accentColour?: string | undefined;
}) => {
  const typeClass =
    accentColour && type === 'primary'
      ? 'hover:bg-black-t-900 active:enabled:shadow-focused'
      : !disabled
        ? typeMap[type]
        : typeMapDisabled[type];

  const className =
    `disabled:text-disabled-text relative rounded-xs group outline-none ${typeClass} ${additionalClassnames ?? ''}`.trim();

  const sizeInPX = sizeMap[size];

  return {
    className,
    style: {
      padding: `${sizeInPX - (className.includes('border') ? 1 : 0)}px`,
    },
  };
};

export const IconButtonInvertedIcon = ({
  Icon,
  size,
  iconClassName,
}: Pick<IconButtonClassnameProps, 'size'> & {
  Icon: NonNullable<IconButtonProps['Icon']>;
  iconClassName?: string | undefined;
}) => (
  <div className="p-0.5">
    <Icon
      className={`${iconSizeMap[size]} stroke-[#252525] group-disabled:stroke-text-disabled ${
        iconClassName ?? ''
      }`}
    />
  </div>
);

export const IconButtonInverted = ({
  Icon,
  children,
  onClick,
  size = 'medium',
  type = 'basic',
  disabled,
  tooltip,
  tooltipId,
  active,
  iconClassName,
  className,
  isSubmit,
  id,
  accentColour,
}: IconButtonProps & { accentColour?: string | undefined }): ReactElement => {
  const ttId = tooltipId ?? tooltip?.replace(/ /g, '-').toLowerCase();

  const hasAccent = !!accentColour && type === 'primary';

  const baseStyles = getIconButtonInvertedStyles({
    type,
    size,
    disabled: !!disabled,
    additionalClassnames: className,
    accentColour,
  });

  return (
    <button
      type={isSubmit ? 'submit' : 'button'}
      data-active={active}
      data-tooltip-place="bottom"
      {...(ttId ? { 'data-tooltip-id': ttId } : {})}
      {...(tooltip ? { 'data-tooltip-content': tooltip } : {})}
      data-tooltip-delay-show={350}
      onClick={onClick}
      disabled={disabled}
      id={id}
      className={baseStyles.className}
      style={baseStyles.style}
    >
      {hasAccent ? (
        <div className="p-0.5">
          <Icon
            className={`${iconSizeMap[size]} group-disabled:stroke-text-disabled ${
              iconClassName ?? ''
            }`}
            style={!disabled ? { stroke: accentColour } : undefined}
          />
        </div>
      ) : (
        <IconButtonInvertedIcon
          Icon={Icon}
          size={size}
          iconClassName={iconClassName}
        />
      )}
      {children}
      {ttId && (
        <Tooltip
          openEvents={{ mouseover: true }}
          closeEvents={{ click: true, mouseleave: true, blur: true }}
          className="z-20"
          id={ttId}
        />
      )}
    </button>
  );
};
